'use client';

import React, { useCallback } from 'react';
import { InquiryFlowLoader, InquiryFlowSkeleton } from '@miso/design-system';
import type { FlowDocument } from '@miso/design-system';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import { mapCounselStartDeparture } from '../model/mapCounselStartDeparture';
import type { CounselStartFlowContext } from '@miso/counsel-schema';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CounselStartFlowAdapterProps {
  visitCounselId: string;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** POST 응답 counsel 로 SWR 캐시를 즉시 갱신하는 함수 (eventual consistency 폴링 fallback 용) */
  mutateWithCounsel?: (counsel: VisitCounselView) => void;
  onAfterStarted: (counsel: VisitCounselView) => void;
  onCancel: () => void;
  /**
   * InquiryFlow의 초기 accumulated 값. 플로우 문서의 initialValueFrom과 함께
   * 사용해 질문 입력 필드를 pre-fill한다.
   *
   * 스키마: CounselStartFlowContext
   *   _prefillAddress — AI 자동기입 주소 필드 묶음
   *     roadAddress: 도로명주소 (필수)
   *     buildingName: 건물명 (optional)
   *     dong: 동 번호/이름 (optional)
   *     unit: 호수 (optional)
   *     floor: 층수 (optional)
   *
   * flow 문서의 각 질문에 initialValueFrom 을 설정해 활성화한다.
   */
  initialValues?: CounselStartFlowContext;
}

// ── Flow fetcher ───────────────────────────────────────────────────────────────

async function fetchFlow(flowId: string): Promise<FlowDocument> {
  const res = await fetch(`/api/v1/flows/${encodeURIComponent(flowId)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error ?? `Flow '${flowId}' 로드 실패`);
  }
  return res.json() as Promise<FlowDocument>;
}

// 모듈 레벨에서 안정된 참조를 유지해 InquiryFlowLoader가 부모 리렌더 시 재fetch하지 않도록 한다.
const fetchCounselStartFlow = () => fetchFlow('counsel-start-flow');

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * counsel-start-flow (주소 입력) 를 production `startVisitCounsel` API 와 연결하는 어댑터.
 *
 * 책임 분리 — 본 어댑터는 "상담 시작" 까지만 담당한다.
 *   - 주소 입력 → POST /start (`startVisitCounsel`) → 상담 state APPOINTED→STARTED 전환
 *   - 집 구조 기록(`startHouseSketchSession`) 은 후속 stage 의 `HouseStructureFlowAdapter` 가 담당
 *
 * 두 가지 진입 시나리오 모두 지원:
 *   A) 플로우 문서가 다른 플로우로 chain 하는 경우 (현재 production: room-record-flow)
 *      → `flowFetcher` 가 chain 을 가로채 `startVisitCounsel` 만 실행하고 무한대기.
 *        SWR 갱신으로 CounselStartPage stage 가 'house-structure' 로 전환되어 본 어댑터가
 *        unmount 되므로 무한대기는 안전하다.
 *   B) 플로우 문서가 자체 terminal 로 종료되는 경우
 *      → `onComplete` 가 `startVisitCounsel` 을 호출.
 *
 * 두 경로 모두 폴링 (1초 × 10회 = 최대 10초) 으로 백엔드 반영을 확인한다.
 */
export function CounselStartFlowAdapter({
  visitCounselId,
  showToast,
  refetch,
  mutateWithCounsel,
  onAfterStarted,
  onCancel,
  initialValues,
}: CounselStartFlowAdapterProps) {
  /**
   * startVisitCounsel 호출 + SWR 즉시 갱신 + 백엔드 반영 폴링 (최대 10초).
   * 시나리오 A/B 양쪽에서 재사용되는 공통 로직.
   */
  const runStartVisitCounsel = useCallback(
    async (
      departure: unknown
    ): Promise<{ counsel: VisitCounselView | null; updated: VisitCounselView | null }> => {
      const departureAddress = mapCounselStartDeparture(departure);
      const updatedCounsel = await myVisitCounselApi.startVisitCounsel(visitCounselId, {
        departureAddress,
      });
      showToast('상담이 시작되었습니다', 'success');

      if (mutateWithCounsel) {
        mutateWithCounsel(updatedCounsel);
      }

      // 짧은 폴링 간격(250ms)으로 동기화 직후 빠르게 다음 단계로 넘어가게 한다.
      // 최대 10초까지 시도 (250ms × 40).
      const POLL_ATTEMPTS = 40;
      const POLL_INTERVAL_MS = 250;
      let confirmed: VisitCounselView | null = null;
      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
        try {
          const result = await refetch();
          if (result?.counsel?.state === 'STARTED') {
            confirmed = result.counsel;
            break;
          }
        } catch {
          // 일시 오류는 무시하고 다음 attempt
        }
        if (attempt < POLL_ATTEMPTS - 1) {
          await new Promise<void>((r) => setTimeout(r, POLL_INTERVAL_MS));
        }
      }

      return { counsel: confirmed ?? updatedCounsel, updated: updatedCounsel };
    },
    [visitCounselId, showToast, refetch, mutateWithCounsel]
  );

  /**
   * 시나리오 A — chain 전환 인터셉트.
   *
   * 현재 production counsel-start-flow 문서는 주소 입력 후 'room-record-flow' 로 chain 한다.
   * 여기서 InquiryFlow 가 chain 을 인식하기 전에 startVisitCounsel 을 호출하고, SWR 캐시 갱신으로
   * CounselStartPage stage 가 'house-structure' 로 전환되어 본 어댑터가 unmount 되도록 한다.
   *
   * 본 어댑터가 unmount 되면 InquiryFlow 도 함께 사라지므로 flowFetcher 가 반환하는 Promise 는
   * 의미가 없다 → 영원히 resolve 하지 않는 Promise 를 반환해 InquiryFlow 가 다음 플로우를
   * 시작하지 않도록 한다.
   */
  const flowFetcher = useCallback(
    async (flowId: string, context: Record<string, unknown>): Promise<FlowDocument> => {
      if (flowId === 'room-record-flow') {
        try {
          await runStartVisitCounsel(context.departure);
        } catch (err) {
          showToast(err instanceof Error ? err.message : '상담 시작에 실패했습니다', 'error');
        }
        // 본 어댑터는 곧 unmount 되므로 InquiryFlow 가 다음 단계로 진행하지 못하도록 영구 대기.
        return new Promise<FlowDocument>(() => {});
      }
      return fetchFlow(flowId);
    },
    [runStartVisitCounsel, showToast]
  );

  /**
   * 시나리오 B — 플로우 문서가 chain 없이 종료될 때 호출되는 fallback.
   * 현재 production 문서는 chain 을 사용하지만, 미래의 단순화된 문서에서도 동작하도록 유지.
   */
  const handleComplete = useCallback(
    async (accumulated: Record<string, unknown>) => {
      try {
        const { counsel } = await runStartVisitCounsel(accumulated.departure);
        if (counsel) {
          onAfterStarted(counsel);
        } else {
          onCancel();
        }
      } catch (err) {
        showToast(err instanceof Error ? err.message : '상담 시작에 실패했습니다', 'error');
        throw err;
      }
    },
    [runStartVisitCounsel, showToast, onAfterStarted, onCancel]
  );

  return (
    <InquiryFlowLoader
      fetcher={fetchCounselStartFlow}
      registries={{ terminals: {} }}
      flowFetcher={flowFetcher}
      initialValues={initialValues}
      onComplete={handleComplete}
      onCancel={onCancel}
      onError={(err) => {
        showToast(err.message ?? '플로우 오류가 발생했습니다', 'error');
        onCancel();
      }}
      skeleton={<InquiryFlowSkeleton />}
    />
  );
}
